import apiClient from '@/lib/apiClient';
import { API_BASE_URL } from '@/config/env';
import { buildListParams, extractPaginatedData } from '@/shared/utils/queryUtils';

export type ExternalAdStatus = 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'NEEDS_EDIT';
export type ExternalAdClassification = 'ROOM_AD' | 'ROOM_WANTED' | 'UNKNOWN';

export type ExternalAd = {
  id: number;
  source: string;
  sourceAdId: string;
  sourceUrl: string | null;
  rawTitle: string | null;
  rawDescription: string | null;
  rawCity: string | null;
  rawDistrict: string | null;
  rawPrice: string | null;
  rawImages: Array<Record<string, any>>;
  localImages: Array<Record<string, any>>;
  matchedKeyword: string | null;
  relevanceScore: number | null;
  relevanceReason: string | null;
  scrapedAt: string | null;
  classificationType: ExternalAdClassification;
  classificationConfidence: number;
  classificationReason: string | null;
  mappedPayload: Record<string, any>;
  status: ExternalAdStatus;
  adminNotes: string | null;
  approvedRoomAdId: number | null;
  approvedRoomWantedAdId: number | null;
  createdAt: string;
  updatedAt: string;
};

export const externalAdsApi = {
  list: async (params: any) => {
    const res = await apiClient.get('/admin/external-ads', { params: buildListParams(params) });
    return extractPaginatedData<ExternalAd>(res);
  },

  getById: async (id: number | string): Promise<ExternalAd> => {
    const res = await apiClient.get(`/admin/external-ads/${id}`);
    return res.data?.data;
  },

  update: async (id: number | string, data: any): Promise<ExternalAd> => {
    const res = await apiClient.patch(`/admin/external-ads/${id}`, data);
    return res.data?.data;
  },

  importHaraj: async () => {
    const res = await apiClient.post('/admin/external-ads/import/haraj');
    return res.data?.data;
  },

  approve: async (id: number | string): Promise<ExternalAd> => {
    const res = await apiClient.post(`/admin/external-ads/${id}/approve`);
    return res.data?.data;
  },

  reject: async (id: number | string, adminNotes?: string): Promise<ExternalAd> => {
    const res = await apiClient.post(`/admin/external-ads/${id}/reject`, { adminNotes });
    return res.data?.data;
  },

  reclassify: async (id: number | string): Promise<ExternalAd> => {
    const res = await apiClient.post(`/admin/external-ads/${id}/reclassify`);
    return res.data?.data;
  },

  imageUrl: (adId: number | string, localPath: string) => {
    const filename = localPath.split('/').filter(Boolean).pop();
    return `${API_BASE_URL}/admin/external-ads/${adId}/images/${encodeURIComponent(filename || '')}`;
  },
};
